import Mention, { MentionNodeAttrs, MentionOptions } from "@tiptap/extension-mention";
import tippy, { Instance, GetReferenceClientRect } from "tippy.js";
import { Content, Node, mergeAttributes, ReactNodeViewRenderer, ReactRenderer } from "@tiptap/react";
import { MentionDropdown } from "./MentionDropdown";
import { PluginKey } from "@tiptap/pm/state";
import {
  getMentionPrefix,
  getMentionPriority,
  IMentionAttributes,
  IMentionExtensionStorage,
  MentionExtensionId,
} from "./utils";
import Suggestion, { SuggestionKeyDownProps, SuggestionOptions, SuggestionProps } from "@tiptap/suggestion";
import { ImplementsInterface } from "libs/type-helpers";
import { DOMOutputSpec } from "@tiptap/pm/model";
import { MentionNodeView } from "./MentionNodeView";
import { DueDateExtension } from "./due-date/DueDateExtension";
import { SuggestionEntryComponent } from "~/components/forms/tiptap/suggestion-utils";

export const MentionExtensionPluginKey = new PluginKey(MentionExtensionId);

export const MentionExtension = (
  Mention as any as Node<CustomizedMentionOptions<any, MentionNodeAttrs>, IMentionExtensionStorage>
).extend({
  name: MentionExtensionId,
  addOptions() {
    return {
      ...this.parent?.(),
      onlyAllowFirstPriorityLevel: false,
      allowMentioningDeactivatedUsers: false,
      // These suggestion options are defined in `@tiptap/extension-mention`, but we are modifying them
      // slightly as described below.
      suggestion: {
        ...this.parent?.().suggestion,
        pluginKey: MentionExtensionPluginKey,
        char: "@",
        allowedPrefixes: [" ", ":"],
        allowSpaces: true,

        // This code is adapted from `@tiptap/extension-mention`, MIT license.
        // https://github.dev/ueberdosis/tiptap
        command: ({ editor, range, props: _props }) => {
          const props = _props as IMentionAttributes;
          // increase range.to by one when the next node is of type "text"
          // and starts with a space character
          const nodeAfter = editor.view.state.selection.$to.nodeAfter;

          const content: Content = [];

          if (props.priority === 100) {
            content.push({
              type: MentionExtensionId,
              attrs: { ...props, dueDate: new Date().toISOString() },
            });
          } else {
            content.push({
              type: MentionExtensionId,
              attrs: props,
            });
          }

          if (props.priority !== 200) {
            const overrideSpace = nodeAfter?.text?.startsWith(" ");

            if (overrideSpace) {
              range.to += 1;
            }

            content.push({
              type: "text",
              text: " ",
            });
          }

          editor.chain().focus().insertContentAt(range, content).run();

          // get reference to `window` object from editor element, to support cross-frame JS usage
          editor.view.dom.ownerDocument.defaultView?.getSelection()?.collapseToEnd();
        },

        // The dropdown component is responsible for loading the items.
        items: () => [],

        render: ({ options }) => {
          let component: SuggestionEntryComponent<{}> | undefined;
          let popup: Instance[] = [];

          const getComponentProps = (props: SuggestionProps<{}>) => ({
            ...props,
            allowMentioningDeactivatedUsers: options.allowMentioningDeactivatedUsers,
            command: (attrs: IMentionAttributes) =>
              props.command({
                ...attrs,
                priority: options.onlyAllowFirstPriorityLevel ? 300 : getMentionPriority(props.query),
              }),
          });

          return {
            onStart: (props) => {
              component = new ReactRenderer(MentionDropdown, {
                props: getComponentProps(props),
                editor: props.editor,
              });

              if (!props.clientRect) return;

              popup = tippy("body", {
                getReferenceClientRect: props.clientRect as GetReferenceClientRect,
                appendTo: () => document.body,
                content: component.element,
                showOnCreate: true,
                interactive: true,
                trigger: "manual",
                placement: "bottom-start",
                zIndex: 150,
              });
            },

            onUpdate(props) {
              const maxPrefixLength = options.onlyAllowFirstPriorityLevel ? 1 : 3;
              const prefix = getMentionPrefix(props.query);

              if (prefix.length > maxPrefixLength) {
                popup[0]?.destroy();
                component?.destroy();
                component = undefined;
                return;
              }

              if (component) {
                component?.updateProps(getComponentProps(props));

                if (!props.clientRect) return;

                popup[0]?.setProps({
                  getReferenceClientRect: props.clientRect as GetReferenceClientRect,
                });
              } else {
                this.onStart?.(props);
              }
            },

            onKeyDown(props) {
              if (props.event.key === "Escape" && component) {
                component?.destroy();
                component = undefined;
                props.event.stopPropagation();
                return true;
              }

              return component?.ref?.onKeyDown(props) || false;
            },

            onExit() {
              popup[0]?.destroy();
              component?.destroy();
            },
          };
        },
      },
    };
  },
  addStorage() {
    return {
      threadId: null,
      mentionWeightAdjustments: {},
    };
  },
  addExtensions() {
    return [DueDateExtension];
  },
  addAttributes() {
    return {
      ...this.parent?.(),

      subject: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-subject"),
        renderHTML: (attributes) => {
          if (!attributes.subject) {
            return {};
          }

          return {
            "data-subject": attributes.subject,
          };
        },
      },

      priority: {
        default: 300,
        parseHTML: (element) => element.getAttribute("data-priority"),
        renderHTML: (attributes) => {
          return {
            "data-priority": attributes.priority,
          };
        },
      },

      dueDate: {
        default: null,
        parseHTML: (element) => {
          const input = element.getAttribute("data-due-date");
          if (!input) return null;
          return input;
        },
        renderHTML: (attributes) => {
          return {
            "data-due-date": attributes.dueDate,
          };
        },
      },

      requestComplete: {
        default: false,
        parseHTML: (element) => element.getAttribute("data-request-complete") === "true",
        renderHTML: (attributes) => {
          return {
            "data-request-complete": attributes.requestComplete,
          };
        },
      },
    };
  },
  parseHTML() {
    return [{ tag: `span[data-type="mention"]` }];
  },
  renderHTML(props) {
    const priority = Number.parseInt(props.node.attrs.priority, 10);

    const prefix =
      priority <= 100 ? "@@@"
      : priority <= 200 ? "@@"
      : "@";

    const nodeTextContent = `${prefix}${props.node.attrs.label}`;

    return [
      "span",
      mergeAttributes({ "data-type": "mention" }, this.options.HTMLAttributes, props.HTMLAttributes),
      nodeTextContent,
    ];
  },
  addNodeView() {
    return ReactNodeViewRenderer(MentionNodeView);
  },
  addProseMirrorPlugins() {
    const render = this.options.suggestion.render;

    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
        render: () => render({ options: this.options }),
      }),
    ];
  },
});

// The "render" option of SuggestionOptions doesn't support having access to the extension
// options object. This makes extending the MentionExtension for use in the SearchEditor difficult.
// To work around this, we've customized the MentionExtension to pass the extension options
// to the render function.
interface CustomizedSuggestionOptions<MentionOptions>
  extends Omit<SuggestionOptions<any, MentionNodeAttrs>, "editor" | "render"> {
  render: (props: { options: MentionOptions }) => {
    onBeforeStart?: ((props: SuggestionProps<any, MentionNodeAttrs>) => void) | undefined;
    onStart?: ((props: SuggestionProps<any, MentionNodeAttrs>) => void) | undefined;
    onBeforeUpdate?: ((props: SuggestionProps<any, MentionNodeAttrs>) => void) | undefined;
    onUpdate?: ((props: SuggestionProps<any, MentionNodeAttrs>) => void) | undefined;
    onExit?: ((props: SuggestionProps<any, MentionNodeAttrs>) => void) | undefined;
    onKeyDown?: ((props: SuggestionKeyDownProps) => boolean) | undefined;
  };
}

type CustomizedMentionOptions<SuggestionItem = any, Attrs = any> = ImplementsInterface<
  MentionOptions,
  {
    HTMLAttributes: Record<string, any>;
    renderLabel: (props: { options: CustomizedMentionOptions<SuggestionItem, Attrs>; node: Node }) => string;
    renderText: (props: { options: CustomizedMentionOptions<SuggestionItem, Attrs>; node: Node }) => string;
    renderHTML: (props: { options: CustomizedMentionOptions<SuggestionItem, Attrs>; node: Node }) => DOMOutputSpec;
    deleteTriggerWithBackspace: boolean;
    suggestion: CustomizedSuggestionOptions<CustomizedMentionOptions>;
    onlyAllowFirstPriorityLevel: boolean;
    allowMentioningDeactivatedUsers: boolean;
  }
>;
